import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import { useEditingContext } from "components/BudgetBuilder/shared/EditingContext";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import { SubawardLineItem } from "components/BudgetBuilder/SponsorCosts/LineItemsList/SubawardLineItem";
import Subsection from "components/Subsection";
import Table from "components/Table";
import { useDictionary } from "hooks";
import { budgetBuilderSelectors, tenantSelectors } from "store/selectors";
import {
	getDirectCostSubtotal,
	getFACostSubtotal,
	getLineItemsDirectCostTotal,
	getNonSubawardLineItemsFACostTotal,
	getPeriodTotalCostSubtotal,
	getSubawardsFACostTotalForPeriod,
	toNumber,
} from "utils/budgetBuilder";

import LineItemsButtons from "./LineItemsButtons";
import LineItemsRow from "./LineItemsRow";
import styles from "./styles.css";


const findChildWithHashInChildren = (category, hash, newLineItem) => {
	for (let j = 0; j < category.items.length; j++) {
		let child = category.items[j];
		if (child.hash === hash) {
			let items = [ ...category.items ];
			items[j] = {
				...child,
				...newLineItem,
			};

			return {
				...category,
				items,
			};
		}
	}

	return false;
};


const LineItemsList = () => {
	const { currencyFormatter } = useTenantFormatter();
	const { sponsorCosts: [ isEditing, setIsEditing ] } = useEditingContext();
	const dictionary = useDictionary("budgetBuilder.components.LineItemsList");

	const [ editedCategories, setEditedCategories ] = useState([]);

	const currentPeriodRate = useSelector(budgetBuilderSelectors.currentPeriodRate);
	const currentPeriodNumber = useSelector(budgetBuilderSelectors.currentPeriodNumber);
	const sponsoredCostsByPeriod = useSelector(budgetBuilderSelectors.sponsoredCostsByPeriod);
	const { subawardCostCap } = useSelector(tenantSelectors.tenant);
	const savedCategories = useSelector(budgetBuilderSelectors.sponsoredCosts);

	const faRateAmount = currentPeriodRate || 0;

	const editedSponsoredCostsByPeriod = useMemo(
		() => ({
			...sponsoredCostsByPeriod,
			[currentPeriodNumber]: editedCategories,
		}),
		[ currentPeriodNumber, editedCategories, sponsoredCostsByPeriod ]
	);

	const isModified = useMemo(
		() => JSON.stringify(editedCategories) !== JSON.stringify(savedCategories),
		[ editedCategories, savedCategories ],
	);

	const subawardLineItemIndex = useMemo(
		() => editedCategories?.findIndex(cat => cat.value === "Subawards"),
		[ editedCategories ],
	);

	const subawardLineItem = useMemo(
		() => editedCategories[subawardLineItemIndex],
		[ editedCategories, subawardLineItemIndex ],
	);

	const directCost = useMemo(
		() => getLineItemsDirectCostTotal(editedCategories),
		[ editedCategories ]
	);
	const faCostTotal = useMemo(
		() => {
			const nonSubawardTotal = getNonSubawardLineItemsFACostTotal(editedCategories, faRateAmount);
			const subawardTotal = getSubawardsFACostTotalForPeriod(
				editedSponsoredCostsByPeriod,
				subawardCostCap,
				currentPeriodNumber,
			);

			return nonSubawardTotal + subawardTotal;
		},
		[ currentPeriodNumber, editedCategories, editedSponsoredCostsByPeriod, faRateAmount, subawardCostCap ]
	);

	const totalCost = useMemo(
		() => toNumber(directCost) + toNumber(faCostTotal),
		[ directCost, faCostTotal ]
	);


	useEffect(() => {
		setEditedCategories(savedCategories);
	}, [ currentPeriodNumber, savedCategories ]);

	useEffect(() => {
		if (editedCategories.length === 0 && savedCategories?.length && savedCategories.length > 0) {
			setEditedCategories(savedCategories);
		}
	}, [ editedCategories.length, savedCategories ]);

	const handleReset = () => setEditedCategories(savedCategories);

	const updateSubawards = newSubawards => {
		let updatedCategories = [ ...editedCategories ];
		updatedCategories[subawardLineItemIndex] = {
			...editedCategories[subawardLineItemIndex],
			...newSubawards,
		};
		setEditedCategories(updatedCategories);
	};

	const updateLineItem = (hash, newLineItem) => {
		let updatedCategories = [ ...editedCategories ];
		for (let i = 0; i < updatedCategories.length; i++) {
			let category = updatedCategories[i];
			if (category.hash === hash) {
				updatedCategories[i] = {
					...category,
					...newLineItem,
				};

				if (isNonEmptyArray(category.items)) {
					category.items.forEach(item => {
						item.isFA = newLineItem.isFA;
					});
				}
			}
			if (isNonEmptyArray(category.items)) {
				const updatedCategory = findChildWithHashInChildren(category, hash, newLineItem);
				if (updatedCategory) {
					updatedCategories[i] = {
						...category,
						...updatedCategory,
					};
					break;
				}
			}
		}
		setEditedCategories(updatedCategories);
	};

	const handleIsExpandedChange = item => {
		let updatedCategories = [ ...editedCategories ];

		for (let i = 0; i < updatedCategories.length; i++) {
			let category = updatedCategories[i];
			if (category.hash === item.hash) {
				let items = [ ...category.items ];
				if (isNonEmptyArray(items)) {
					for (let newItem of items) {
						newItem.isExpanded = !category.isExpanded;
					}
				}
				updatedCategories[i] = {
					...category,
					items,
					isExpanded: !category.isExpanded,
				};
				break;
			}
		}
		setEditedCategories(updatedCategories);
	};

	const renderTableRow = (category, index, childIndex) => (
		<LineItemsRow
			key={category.hash}
			category={category}
			childPosition={childIndex}
			isEditing={isEditing}
			updateLineItem={updateLineItem}
			handleIsExpandedChange={handleIsExpandedChange}
		/>
	);

	const renderCategories = () => editedCategories
		?.filter(category => category.value !== "Subawards")
		?.sort((a, b) => a.sort - b.sort).map((category, index) => (
			<Fragment key={index}>
				{renderTableRow(category, index)}
				{category.items &&
					category.items
						.sort((a, b) => a.sort - b.sort)
						.map((item, childIndex) => renderTableRow(item, index, childIndex))}
				{category.items &&
					(
						<Table.Row className={`${styles.rowHeader} ${styles.row}`}>
							<Table.Cell className={styles.rowHeaderCell}>
								<span className={styles.bold}>{`${category.value} Subtotal`}</span>
							</Table.Cell>

							<Table.Cell className={`${styles.rowHeaderCell} ${styles.cellAlignRight}`}>
								<span className={styles.bold}>
									{currencyFormatter(getDirectCostSubtotal(category))}
								</span>
							</Table.Cell>
							<Table.Cell className={styles.rowHeaderCell}>
								{""}
							</Table.Cell>
							<Table.Cell className={styles.rowHeaderCell}>
								{""}
							</Table.Cell>
							<Table.Cell
								className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}
							>
								<span className={styles.bold}>
									{currencyFormatter(getFACostSubtotal(category, faRateAmount))}
								</span>
							</Table.Cell>
							<Table.Cell
								className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}
							>
								<span className={styles.bold}>
									{currencyFormatter(getPeriodTotalCostSubtotal(category, faRateAmount))}
								</span>
							</Table.Cell>
							<Table.Cell className={styles.rowHeaderCell}>
							</Table.Cell>
						</Table.Row>
					)}
			</Fragment>
		));

	return (
		<Subsection
			title={dictionary.header || "Line Items"}
			buttons={LineItemsButtons({ editedCategories, isEditing, isModified, handleReset, setIsEditing })}
		>
			<Table captionText="Line Items">
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell width="four" className={styles.cellAlignLeft}>
							{dictionary.lineItem || "Line Item"}
						</Table.HeaderCell>
						<Table.HeaderCell width="two" className={styles.cellAlignRight}>
							{dictionary.directCost || "Direct Cost"}
						</Table.HeaderCell>
						<Table.HeaderCell width="one" className={styles.cellAlignRight}>
							{dictionary.includeFA || "Include F&A"}
						</Table.HeaderCell>
						<Table.HeaderCell width="two" className={styles.cellAlignRight}>
							{dictionary.faRate || "F&A Rate"}
						</Table.HeaderCell>
						<Table.HeaderCell width="two" className={styles.cellAlignRight}>
							{dictionary.faCost || "F&A Cost"}
						</Table.HeaderCell>
						<Table.HeaderCell width="two" className={styles.cellAlignRight}>
							{dictionary.periodTotalCost || "Period Total Cost"}
						</Table.HeaderCell>
						<Table.HeaderCell width="one" className={styles.cellAlignCenter}>

						</Table.HeaderCell>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{renderCategories()}
					{subawardLineItem && (
						<SubawardLineItem
							isEditing={isEditing}
							lineItem={subawardLineItem}
							onChange={updateSubawards}
							editedSponsoredCostsByPeriod={editedSponsoredCostsByPeriod}
						/>
					)}
					<Table.Row className={`${styles.rowHeader} ${styles.row} ${styles.totalRowPadding}`}>
						<Table.Cell className={`${styles.rowHeaderCell} ${styles.totalCellPadding}`}>
							<span className={styles.bold}>{dictionary.lineItemsTotal || "Line Items Total"}</span>
						</Table.Cell>
						<Table.Cell className={`${styles.rowHeaderCell} ${styles.cellAlignRight}`}>
							<span className={`${styles.bold} ${styles.cellAlignRight}`}>
								{currencyFormatter(directCost)}
							</span>
						</Table.Cell>
						<Table.Cell className={styles.rowHeaderCell} />
						<Table.Cell className={styles.rowHeaderCell} />
						<Table.Cell
							className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}
						>
							<span className={`${styles.bold} ${styles.cellAlignRight}`}>
								{currencyFormatter(faCostTotal)}
							</span>
						</Table.Cell>
						<Table.Cell
							className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}
						>
							<span
								className={`${styles.bold} ${styles.cellAlignRight}`}
							>{currencyFormatter(totalCost)}</span>
						</Table.Cell>
						<Table.Cell className={styles.rowHeaderCell} />
					</Table.Row>
				</Table.Body>
			</Table>
		</Subsection>
	);
};

export default LineItemsList;
